import {
  Callout,
  FormGroup,
  Icon,
  Intent,
  PanelProps,
  Position,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import {
  GenericProperty,
  GenericPropertySuggestion,
  GenericPropertySuggestionType,
  ManagedGenericPropertiesEditor,
  StringInput,
  Tooltip,
} from 'vet-bones/components';
import { isEqual } from 'vet-bones/utils';

import { addDataSource } from 'src/actions/data-sources/action-creators/addDataSource';
import { AdvancedOptions } from 'src/components/AdvancedOptions';
import {
  DataSourceOption,
  DataSourceType,
  JDBC_DRIVER_KEY,
  STANDARD_JDBC_PROPERTY_KEYS,
  defaultTypeDescription,
} from 'src/constants/dataSourceTypeDescription';
import { MAX_PROPERTY_SUGGESTION_ITEMS } from 'src/constants/limits';
import { DATA_SOURCE_OPTIONS_DOCUMENTATION_LINK } from 'src/constants/links';
import {
  TEST_IDS,
  getDataSourceCreationPanelAdvancedPropertyTestId,
  getDataSourceStandardOptionTestId,
} from 'src/constants/testIds';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import { useValidateDataSourceName } from 'src/containers/ProjectResourceDialog/ProjectFileUploadCreationPanel/hooks';
import {
  ProjectVirtualGraphCreationPanel,
  ProjectVirtualGraphCreationPanelProps,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/ProjectVirtualGraphCreationPanel';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcePropertiesSelector } from 'src/selectors/dataSourceProperties';
import { SqlDataSource } from 'src/store/state/dataSources';
import * as copy from 'src/templates/copy';
import {
  countUniqueExistingProperties,
  genericPropertyValidator,
  isRowInvalid,
} from 'src/utils/genericPropertyValidation';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const DATA_SOURCE_NAME = 'DATA_SOURCE_NAME';

type StandardPropertyFieldProps = {
  description?: string;
  label: string;
  setValue: (value: string) => void;
  testId: string;
  errorValue?: string;
  value: string;
};

const StandardPropertyField: React.VFC<StandardPropertyFieldProps> = ({
  description,
  label,
  errorValue,
  setValue,
  testId,
  value,
}) => {
  const toolTipContent = (value && errorValue) || description || '';
  const [isToolTipOpen, setIsToolTipOpen] = React.useState(false);
  const isObfuscated =
    toolTipContent ===
    defaultTypeDescription.DATA_SOURCE_OPTIONS[DataSourceOption.PASSWORD]
      .description;

  return (
    <div
      className="sd-data-source-panel-properties__standard-property"
      onMouseEnter={() => setIsToolTipOpen(true)}
      onMouseLeave={() => setIsToolTipOpen(false)}
    >
      <FormGroup inline label={label}>
        <StringInput
          intent={value && errorValue ? Intent.DANGER : Intent.NONE}
          // Obfuscate text for password
          onSetValue={setValue}
          testId={testId}
          type={isObfuscated ? 'password' : 'text'}
          value={value}
        />
        <Tooltip
          content={toolTipContent}
          disabled={!toolTipContent}
          fill
          isOpen={isToolTipOpen}
          position={Position.BOTTOM}
        />
      </FormGroup>
    </div>
  );
};

export type DataSourceCreationPanelProps = ProjectResourceDialogPanelProps & {
  type: DataSourceType;
};

export const DataSourceCreationPanel: React.VFC<
  PanelProps<DataSourceCreationPanelProps>
> = ({ closePanel, openPanel, setFooterProps, type }) => {
  const isMounted = useIsMounted();

  const connection = useReduxSelector(connectionSelector);
  const dispatch = useReduxDispatch();
  const isPendingDataSourceCreation = useReduxSelector(
    (state) => state.dataSources.pendingAddDataSource
  );
  const propertyDescriptions = useReduxSelector(
    dataSourcePropertiesSelector(connection, type),
    isEqual
  );

  const [dataSourceName, setDataSourceName] = React.useState<string>('');
  const [standardPropertyValues, setStandardPropertyValues] = React.useState<
    Record<string, string>
  >(
    Array.from(STANDARD_JDBC_PROPERTY_KEYS).reduce(
      (agg, property) => ({
        ...agg,
        [property]: propertyDescriptions[property]?.default || '',
      }),
      {}
    )
  );

  const [advancedProperties, setAdvancedProperties] = React.useState<
    GenericProperty[]
  >([]);
  const advancedPropertySuggestions = React.useMemo<
    GenericPropertySuggestion[]
  >(() => {
    const existingAdvancedProperties: string[] =
      Object.values(DataSourceOption);
    return Object.entries(propertyDescriptions)
      .filter(
        ([property]) =>
          !(property in DataSourceOption) &&
          property !== JDBC_DRIVER_KEY &&
          !existingAdvancedProperties.includes(property)
      )
      .map(([property, propertyDescription]) => {
        const suggestion: GenericPropertySuggestion = {
          name: property,
          description: propertyDescription?.description,
          defaultValue:
            propertyDescription?.default !== undefined
              ? `${propertyDescription?.default}`
              : undefined,
          type:
            (propertyDescription?.type?.toUpperCase() as GenericPropertySuggestionType) ||
            GenericPropertySuggestionType.STRING,
        };
        return suggestion;
      });
  }, [propertyDescriptions]);
  const [
    hasAttemptedToCreateInvalidNewRow,
    setHasAttemptedToCreateInvalidNewRow,
  ] = React.useState(false);

  const validateName = useValidateDataSourceName();

  const dataSourceNameError = validateName(dataSourceName);

  const {
    invalidDataSourcePropertyNames,
    missingDataSourcePropertyNames,
    newDataSourceOptions,
  } = React.useMemo(() => {
    const invalidDataSourcePropertyNames = new Set<string>();
    const missingDataSourcePropertyNames = new Set<string>();
    const newDataSourceOptions: Record<string | string, any> = {
      // not "required" for creation, but sets type on Stardog (otherwise Generic)
      [JDBC_DRIVER_KEY]: propertyDescriptions?.[JDBC_DRIVER_KEY]?.default,
    };
    Object.entries(DataSourceOption).forEach(([propertyNameArg, property]) => {
      const propertyName = propertyNameArg as keyof typeof DataSourceOption;

      newDataSourceOptions[property] = standardPropertyValues[property];
      if (
        propertyDescriptions[property]?.required &&
        !newDataSourceOptions[property]
      ) {
        missingDataSourcePropertyNames.add(
          copy.components.projectResourceDialog.projectDataSourceCreationPanel
            .propertyLabels[propertyName]
        );
      }
    });
    const existingPropertyCounts =
      countUniqueExistingProperties(advancedProperties);
    advancedProperties.forEach(({ name, value }) => {
      newDataSourceOptions[name] = value;
      if (
        isRowInvalid(
          name,
          value,
          STANDARD_JDBC_PROPERTY_KEYS,
          existingPropertyCounts
        )
      ) {
        invalidDataSourcePropertyNames.add(name);
      }
    });
    return {
      invalidDataSourcePropertyNames,
      missingDataSourcePropertyNames,
      newDataSourceOptions,
    };
  }, [propertyDescriptions, advancedProperties, standardPropertyValues]);

  const handleCreateButtonClick = React.useCallback(async () => {
    if (!connection) {
      return;
    }

    const newDataSource: SqlDataSource = {
      entityName: '',
      available: true,
      sharable: true,
      id: dataSourceName,
      type,
      options: newDataSourceOptions as SqlDataSource['options'],
      tables: [],
      tableMetadataErrorResponse: null,
    };

    const didSucceed = await dispatch(addDataSource(newDataSource));

    if (!didSucceed) {
      return;
    }

    const props: ProjectVirtualGraphCreationPanelProps = {
      setFooterProps,
      dataSourceId: newDataSource.id,
    };
    closePanel();
    openPanel({
      renderPanel: ProjectVirtualGraphCreationPanel,
      props,
    });
  }, [
    closePanel,
    connection,
    dataSourceName,
    dispatch,
    newDataSourceOptions,
    openPanel,
    setFooterProps,
    type,
  ]);

  React.useEffect(() => {
    if (isMounted.current) {
      let createButtonsError = '';
      if (dataSourceNameError) {
        createButtonsError = dataSourceNameError;
      } else if (invalidDataSourcePropertyNames.size) {
        createButtonsError =
          copy.components.projectResourceDialog.projectDataSourceCreationPanel.invalidDataSourceProperties(
            Array.from(invalidDataSourcePropertyNames.values())
          );
      } else if (missingDataSourcePropertyNames.size) {
        createButtonsError =
          copy.components.projectResourceDialog.projectDataSourceCreationPanel.missingDataSourceProperties(
            Array.from(missingDataSourcePropertyNames.values())
          );
      }

      setFooterProps({
        createButtonsError,
        handleBackButtonClick: closePanel,
        handleCreateButtonClick,
      });
    }
  }, [
    closePanel,
    dataSourceNameError,
    handleCreateButtonClick,
    invalidDataSourcePropertyNames,
    isMounted,
    missingDataSourcePropertyNames,
    setFooterProps,
  ]);

  if (isPendingDataSourceCreation) {
    return (
      <Callout className="sd-data-source-panel" intent={Intent.NONE}>
        <Spinner size={SpinnerSize.LARGE} />;
      </Callout>
    );
  }

  return (
    <DataSourcePanelContainer type={type}>
      <div className={classNames('sd-data-source-panel-properties')}>
        <div className="sd-data-source-panel-properties__standard-properties">
          <StandardPropertyField
            errorValue={dataSourceNameError}
            label={
              copy.components.projectResourceDialog
                .projectDataSourceCreationPanel.dataSourceNameLabel
            }
            setValue={setDataSourceName}
            testId={getDataSourceStandardOptionTestId(DATA_SOURCE_NAME)}
            value={dataSourceName}
          />
          {(
            Object.entries(DataSourceOption) as Array<
              [keyof typeof DataSourceOption, string]
            >
          ).map(([propertyType, property]) => (
            <StandardPropertyField
              key={property}
              description={propertyDescriptions[property]?.description}
              label={
                copy.components.projectResourceDialog
                  .projectDataSourceCreationPanel.propertyLabels[propertyType]
              }
              setValue={(value: string) =>
                setStandardPropertyValues({
                  ...standardPropertyValues,
                  [property]: value,
                })
              }
              testId={getDataSourceStandardOptionTestId(property)}
              value={standardPropertyValues[property]}
            />
          ))}
        </div>
        <AdvancedOptions
          buttonTestId={
            TEST_IDS.projectResourceDialog.dataSourceCreationPanel
              .advancedPropertiesButton
          }
          rightElement={
            <a
              className="sd-data-source-panel-properties__advanced-docs-link"
              href={DATA_SOURCE_OPTIONS_DOCUMENTATION_LINK}
              rel="noreferrer"
              target="_blank"
            >
              <Icon icon={IconNames.INFO_SIGN} />
            </a>
          }
          text={() =>
            copy.components.projectResourceDialog.projectDataSourceCreationPanel.advancedOptions(
              advancedProperties.length
            )
          }
        >
          <ManagedGenericPropertiesEditor
            editorTestId={
              TEST_IDS.projectResourceDialog.dataSourceCreationPanel
                .advancedPropertiesEditor
            }
            maxSuggestions={MAX_PROPERTY_SUGGESTION_ITEMS}
            onPropertiesUpdated={setAdvancedProperties}
            preventAddingNewProperty={({ name, value }) => {
              const invalid = isRowInvalid(
                name,
                value,
                STANDARD_JDBC_PROPERTY_KEYS,
                countUniqueExistingProperties(advancedProperties, name)
              );
              setHasAttemptedToCreateInvalidNewRow(invalid);
              return invalid;
            }}
            properties={advancedProperties}
            propertyActionTestId={
              TEST_IDS.projectResourceDialog.dataSourceCreationPanel
                .advancedProperties.addButton
            }
            propertyNameTestId={
              TEST_IDS.projectResourceDialog.dataSourceCreationPanel
                .advancedProperties.newName
            }
            propertySuggestions={advancedPropertySuggestions}
            propertyValueTestId={
              TEST_IDS.projectResourceDialog.dataSourceCreationPanel
                .advancedProperties.newValue
            }
            rowTestId={getDataSourceCreationPanelAdvancedPropertyTestId}
            validator={genericPropertyValidator(
              hasAttemptedToCreateInvalidNewRow,
              STANDARD_JDBC_PROPERTY_KEYS,
              advancedProperties
            )}
          />
        </AdvancedOptions>
      </div>
    </DataSourcePanelContainer>
  );
};
